import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { PublicLayout } from '@/components/public/PublicLayout';
import { SilaHero } from '@/components/public/SilaHero';
import { useLivePageContent } from '@/hooks/usePageContent';
import { Seo } from '@/components/Seo';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { EventsCalendar } from '@/components/public/EventsCalendar';
import { EventTicketDialog } from '@/components/public/EventTicketDialog';
import { eventsService } from '@/services/events.service';
import { mediaUrl } from '@/lib/api';
import { setCategories, EVENTS_HERO_IMG, dayKey, euro, fmtLongDate, fmtShortDate, categoryMeta, catColor } from '@/lib/eventsMock';
import {
  CalendarHeart, CalendarDays, LayoutGrid, MapPin, Clock, Search, Ticket, ArrowRight, Sparkles, Star, CalendarX2, ExternalLink,
} from 'lucide-react';

const reveal = {
  initial: { opacity: 0, y: 14 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-60px' },
  transition: { duration: 0.45, ease: [0.16, 1, 0.3, 1] },
};

const coverOf = (c) => (c ? mediaUrl(c) : EVENTS_HERO_IMG);
const str = (v) => (typeof v === 'string' || typeof v === 'number' ? String(v) : '');

/* Normalizza il dettaglio backend nella forma attesa dal dialog di acquisto. */
export const toDialogEvent = (det) => ({
  id: det.id, slug: det.slug, title: det.title, category: det.category,
  cover: coverOf(det.cover), location: det.location || '', description: det.description || '', short: det.short || '',
  sessions: (det.sessions || []).map((s) => ({ id: s.id, date: new Date(s.start), time: s.time, left: s.left })),
  ticketTypes: (det.ticket_types || []).map((t) => ({ id: t.id, label: t.label, price: t.price, desc: t.desc || '' })),
});

function EventCard({ e, onOpen, onPage }) {
  const meta = categoryMeta(e.category);
  return (
    <motion.div {...reveal} className="group flex flex-col overflow-hidden rounded-2xl border border-border bg-card text-left shadow-sm transition-transform duration-300 hover:-translate-y-1 hover:shadow-md" data-testid={`eventi-event-card-${e.slug}`}>
      <button type="button" onClick={() => onOpen(e)} className="block text-left focus-visible:outline-none">
        <div className="relative h-48 overflow-hidden bg-muted">
          <img src={coverOf(e.cover)} alt={e.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.05]" />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0) 45%, rgba(0,0,0,0.55) 100%)' }} />
          <span className="absolute left-3 top-3 inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold text-white backdrop-blur" style={{ backgroundColor: catColor(e.category, 0.85) }}>{meta.label}</span>
          <span className="absolute right-3 top-3 rounded-full bg-background/90 px-3 py-1 text-xs font-semibold text-foreground shadow-sm backdrop-blur">da {euro(e.price_from)}</span>
          {e.dateObj && (
            <div className="absolute bottom-3 left-3 flex items-center gap-2 rounded-full bg-background/90 px-2.5 py-1 text-xs font-semibold text-foreground shadow-sm backdrop-blur">
              <CalendarDays className="h-3.5 w-3.5" style={{ color: catColor(e.category) }} />
              <span className="capitalize">{fmtShortDate(e.dateObj)}</span>
              {e.time && (<><span className="text-muted-foreground">·</span><Clock className="h-3.5 w-3.5" /> {e.time}</>)}
            </div>
          )}
        </div>
      </button>
      <div className="flex flex-1 flex-col p-4">
        <h3 className="font-display text-lg font-semibold leading-snug text-foreground line-clamp-1">{e.title}</h3>
        <p className="mt-1 flex items-center gap-1.5 text-sm text-muted-foreground"><MapPin className="h-4 w-4" style={{ color: catColor(e.category) }} />{e.location || 'Sila'}</p>
        <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">{e.short}</p>
        <div className="mt-3 flex items-center justify-between pt-2">
          <button type="button" onClick={() => onOpen(e)} className="flex items-center gap-1 text-sm font-semibold text-primary" data-testid={`eventi-card-buy-${e.slug}`}>
            Dettagli e biglietti <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </button>
          <button type="button" onClick={() => onPage(e)} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" data-testid={`eventi-card-page-${e.slug}`}>
            Pagina <ExternalLink className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </motion.div>
  );
}

function DayEventRow({ e, onOpen }) {
  const meta = categoryMeta(e.category);
  return (
    <button type="button" onClick={() => onOpen(e)} data-testid={`eventi-day-panel-event-${e.slug}`}
      className="group flex w-full items-center gap-3 rounded-xl border border-border bg-card p-2.5 text-left transition-colors hover:bg-accent/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
      <div className="relative h-14 w-14 shrink-0 overflow-hidden rounded-lg bg-muted">
        <img src={coverOf(e.cover)} alt="" className="h-full w-full object-cover" />
        <span className="absolute inset-x-0 bottom-0 h-1" style={{ backgroundColor: catColor(e.category) }} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-1.5 w-1.5 rounded-full" style={{ backgroundColor: catColor(e.category) }} />
          <span className="truncate text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{meta.label}</span>
        </div>
        <p className="truncate text-sm font-semibold text-foreground">{e.title}</p>
        <p className="flex items-center gap-2 text-xs text-muted-foreground">{e.time && (<><Clock className="h-3 w-3" />{e.time}<span>·</span></>)}<MapPin className="h-3 w-3" /><span className="truncate">{e.location || 'Sila'}</span></p>
      </div>
      <span className="shrink-0 text-sm font-semibold text-primary">da {euro(e.price_from)}</span>
    </button>
  );
}

export default function Eventi() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [rawEvents, setRawEvents] = useState([]);
  const [cats, setCats] = useState([]);
  const [view, setView] = useState('calendario');
  const [activeCat, setActiveCat] = useState('tutte');
  const [q, setQ] = useState('');
  const [month, setMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [selectedDate, setSelectedDate] = useState(null);
  const [detail, setDetail] = useState(null);
  const ov = useLivePageContent('eventi');

  const today = useMemo(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; }, []);

  const load = useCallback(async () => {
    setLoading(true); setError(false);
    try {
      const data = await eventsService.publicList();
      setCategories(data.categories || []);
      setCats(data.categories || []);
      setRawEvents((data.events || []).map((e) => ({
        ...e,
        dateObj: e.date ? new Date(e.date) : null,
        sessionDates: (e.session_dates || []).map((d) => new Date(d)),
      })));
    } catch (err) {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => rawEvents
    .filter((e) => activeCat === 'tutte' || e.category === activeCat)
    .filter((e) => !q || `${e.title} ${e.location || ''}`.toLowerCase().includes(q.toLowerCase())), [rawEvents, activeCat, q]);

  const sorted = useMemo(() => [...filtered].sort((a, b) => (a.dateObj?.getTime() || 9e15) - (b.dateObj?.getTime() || 9e15)), [filtered]);

  const eventsByDay = useMemo(() => {
    const m = {};
    filtered.forEach((e) => { (e.sessionDates.length ? e.sessionDates : (e.dateObj ? [e.dateObj] : [])).forEach((d) => { const k = dayKey(d); (m[k] = m[k] || []).push(e); }); });
    return m;
  }, [filtered]);

  const featured = useMemo(() => sorted.find((e) => e.dateObj && e.dateObj >= today) || sorted[0], [sorted, today]);
  const effectiveSelected = selectedDate || (featured?.dateObj) || today;
  const dayEvents = useMemo(() => (eventsByDay[dayKey(effectiveSelected)] || []).slice(), [eventsByDay, effectiveSelected]);

  const prevMonth = () => setMonth((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1));
  const nextMonth = () => setMonth((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1));
  const resetFilters = () => { setActiveCat('tutte'); setQ(''); };

  const openDialog = useCallback(async (e) => {
    try {
      const det = await eventsService.publicGet(e.slug || e.id);
      setDetail(toDialogEvent(det));
    } catch (err) {
      toast.error('Evento non disponibile');
    }
  }, []);
  const openPage = useCallback((e) => navigate(`/eventi/${e.slug}`), [navigate]);

  return (
    <PublicLayout>
      <Seo title="Eventi — Sila Experiences" description="Eventi olistici, sportivi, culturali, naturalistici, enogastronomici e molto altro nel cuore della Sila. Scopri il calendario e acquista il tuo biglietto." />

      <SilaHero
        image={coverOf(featured?.cover) || str(ov.heroImage) || undefined}
        badge={featured ? `In evidenza · ${featured.dateObj ? fmtShortDate(featured.dateObj) : ''}` : (str(ov.eyebrow) || 'Eventi')}
        title={featured ? featured.title : (str(ov.title) || 'Eventi nel cuore della Sila')}
        subtitle={featured ? `${featured.dateObj ? fmtLongDate(featured.dateObj) : ''}${featured.time ? ` · ${featured.time}` : ''}${featured.location ? ` · ${featured.location}` : ''}` : (str(ov.lead) || 'Eventi olistici, sportivi, culturali, naturalistici ed enogastronomici.')}
        icon={CalendarHeart}
        minH="64vh"
        testId="eventi-hero"
      >
        <div className="flex flex-wrap items-center gap-3">
          {featured && (
            <Button size="lg" className="rounded-full px-6 text-sm font-semibold" onClick={() => openDialog(featured)} data-testid="eventi-hero-featured-primary-cta">
              <Ticket className="mr-2 h-4 w-4" /> Acquista il biglietto · da {euro(featured.price_from)}
            </Button>
          )}
          <Button size="lg" variant="secondary" className="rounded-full px-6 text-sm font-semibold" onClick={() => document.getElementById('eventi-content')?.scrollIntoView({ behavior: 'smooth' })} data-testid="eventi-hero-featured-secondary-cta">
            <CalendarDays className="mr-2 h-4 w-4" /> Esplora il calendario
          </Button>
        </div>
      </SilaHero>

      <div id="eventi-content" className="sila-section py-10 sm:py-12">
        {/* BARRA FILTRI STICKY */}
        <div className="sticky top-[72px] z-30 -mx-4 px-4 sm:mx-0 sm:px-0">
          <div className="rounded-2xl border border-border/70 bg-background/80 p-3 shadow-sm backdrop-blur-md">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1 lg:pb-0" data-testid="eventi-category-pill-group">
                <button type="button" onClick={() => setActiveCat('tutte')} data-testid="eventi-category-pill-tutte"
                  className={`shrink-0 rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors ${activeCat === 'tutte' ? 'border-transparent bg-primary text-primary-foreground shadow-sm' : 'border-border bg-card/70 text-muted-foreground hover:bg-accent/50'}`}>
                  Tutte
                </button>
                {cats.map((c) => {
                  const active = activeCat === c.slug;
                  return (
                    <button key={c.slug} type="button" onClick={() => setActiveCat(c.slug)} data-testid={`eventi-category-pill-${c.slug}`}
                      className={`flex shrink-0 items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors ${active ? 'border-transparent text-white shadow-sm' : 'border-border bg-card/70 text-muted-foreground hover:bg-accent/50'}`}
                      style={active ? { backgroundColor: catColor(c.slug) } : undefined}>
                      <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: active ? '#fff' : catColor(c.slug) }} />
                      {c.label}
                    </button>
                  );
                })}
              </div>
              <div className="flex items-center gap-2">
                <div className="relative flex-1 lg:w-56 lg:flex-none">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca luogo o evento" className="h-10 rounded-full pl-9" data-testid="eventi-search-input" />
                </div>
                <ToggleGroup type="single" value={view} onValueChange={(v) => v && setView(v)} className="shrink-0 rounded-full border border-border bg-card p-1" data-testid="eventi-view-toggle">
                  <ToggleGroupItem value="calendario" className="h-8 gap-1.5 rounded-full px-3 text-xs data-[state=on]:bg-primary data-[state=on]:text-primary-foreground" data-testid="eventi-view-calendario" aria-label="Vista calendario">
                    <CalendarDays className="h-4 w-4" /> <span className="hidden sm:inline">Calendario</span>
                  </ToggleGroupItem>
                  <ToggleGroupItem value="griglia" className="h-8 gap-1.5 rounded-full px-3 text-xs data-[state=on]:bg-primary data-[state=on]:text-primary-foreground" data-testid="eventi-view-griglia" aria-label="Vista griglia">
                    <LayoutGrid className="h-4 w-4" /> <span className="hidden sm:inline">Griglia</span>
                  </ToggleGroupItem>
                </ToggleGroup>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 flex items-end justify-between gap-4">
          <div>
            <h2 className="font-display text-2xl font-bold tracking-tight text-foreground sm:text-3xl">Calendario Eventi</h2>
            <p className="mt-1 text-sm text-muted-foreground">Scopri cosa succede in Sila e prenota il tuo biglietto in pochi secondi.</p>
          </div>
          {!loading && <span className="hidden shrink-0 rounded-full bg-accent px-3 py-1 text-sm font-medium text-accent-foreground sm:inline">{filtered.length} eventi</span>}
        </div>

        {/* STATI */}
        {loading ? (
          <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3" data-testid="eventi-loading">
            {[0, 1, 2, 3, 4, 5].map((i) => (<div key={i} className="overflow-hidden rounded-2xl border border-border bg-card"><Skeleton className="h-48 w-full" /><div className="space-y-2 p-4"><Skeleton className="h-5 w-2/3" /><Skeleton className="h-4 w-1/2" /><Skeleton className="h-4 w-full" /></div></div>))}
          </div>
        ) : error ? (
          <div className="mt-8 rounded-2xl border border-dashed border-border bg-muted/40 p-12 text-center" data-testid="eventi-error-state">
            <CalendarX2 className="mx-auto h-10 w-10 text-muted-foreground" />
            <p className="mt-3 font-medium text-foreground">Non riusciamo a caricare gli eventi</p>
            <Button variant="outline" className="mt-4 rounded-full" onClick={load} data-testid="eventi-retry">Riprova</Button>
          </div>
        ) : filtered.length === 0 ? (
          <div className="mt-8 rounded-2xl border border-dashed border-border bg-muted/40 p-12 text-center" data-testid="eventi-empty-state">
            <CalendarX2 className="mx-auto h-10 w-10 text-muted-foreground" />
            <p className="mt-3 font-medium text-foreground">{rawEvents.length === 0 ? 'Nessun evento in programma al momento' : 'Nessun evento per questi filtri'}</p>
            <p className="text-sm text-muted-foreground">{rawEvents.length === 0 ? 'Torna presto: nuovi eventi in arrivo nel cuore della Sila.' : 'Prova a cambiare categoria o a rimuovere la ricerca.'}</p>
            {rawEvents.length > 0 && <Button variant="outline" className="mt-4 rounded-full" onClick={resetFilters} data-testid="eventi-empty-reset-filters">Mostra tutti</Button>}
          </div>
        ) : view === 'calendario' ? (
          <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-12">
            <div className="lg:col-span-8">
              <EventsCalendar month={month} eventsByDay={eventsByDay} selectedDate={effectiveSelected} onSelectDate={setSelectedDate} onPrev={prevMonth} onNext={nextMonth} categories={cats} />
            </div>
            <div className="lg:col-span-4" data-testid="eventi-day-panel">
              <div className="rounded-2xl border border-border bg-card p-4 shadow-sm sm:p-5">
                <div className="flex items-center gap-2">
                  <CalendarHeart className="h-5 w-5 text-primary" />
                  <h3 className="font-display text-lg font-semibold capitalize text-foreground">{fmtLongDate(effectiveSelected)}</h3>
                </div>
                <p className="mt-0.5 text-sm text-muted-foreground">{dayEvents.length ? `${dayEvents.length} evento${dayEvents.length > 1 ? 'i' : ''} in programma` : 'Nessun evento in questa data'}</p>
                <div className="mt-4">
                  {dayEvents.length ? (
                    <ScrollArea className="h-[360px] pr-3"><div className="space-y-2.5">{dayEvents.map((e) => <DayEventRow key={e.id} e={e} onOpen={openDialog} />)}</div></ScrollArea>
                  ) : (
                    <div className="flex flex-col items-center rounded-xl border border-dashed border-border bg-muted/30 p-8 text-center">
                      <Sparkles className="h-8 w-8 text-muted-foreground" />
                      <p className="mt-2 text-sm text-muted-foreground">Seleziona un giorno con un pallino colorato per vedere gli eventi.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3" data-testid="eventi-grid">
            {sorted.map((e) => <EventCard key={e.id} e={e} onOpen={openDialog} onPage={openPage} />)}
          </div>
        )}

        {/* STRISCIA FIDUCIA */}
        <div className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-3">
          {[
            { icon: Ticket, t: 'Biglietto immediato', d: 'Ricevi subito il QR, pronto all’ingresso.' },
            { icon: Star, t: 'Eventi selezionati', d: 'Esperienze verificate nel Parco della Sila.' },
            { icon: CalendarHeart, t: 'Prezzo trasparente', d: 'Nessuna sorpresa: prezzo calcolato lato server.' },
          ].map((f, i) => (
            <motion.div key={i} {...reveal} className="flex items-start gap-3 rounded-2xl border border-border bg-card p-4 shadow-sm">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl" style={{ backgroundColor: 'hsl(var(--primary) / 0.12)' }}>
                <f.icon className="h-5 w-5" style={{ color: 'hsl(var(--primary))' }} />
              </span>
              <div><p className="text-sm font-semibold text-foreground">{f.t}</p><p className="text-sm text-muted-foreground">{f.d}</p></div>
            </motion.div>
          ))}
        </div>
      </div>

      <EventTicketDialog event={detail} open={!!detail} onClose={() => setDetail(null)} />
    </PublicLayout>
  );
}
